import { useState } from 'react';
import { formaterDateHeure } from '@gsm/partage';
import { useRessource, messageErreur } from '../api/ressource';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Pastille, type TonPastille } from '../composants/Pastille';
import { BarreFiltres, FiltreSegments, Pagination } from '../composants/Filtres';

interface Ecriture {
  id: string;
  horodatage: string;
  entite: string;
  entiteId: string | null;
  action: string;
  adresseIp: string | null;
  commentaire: string | null;
  utilisateur: { login: string; nom: string; prenom: string } | null;
}

interface Page {
  total: number;
  page: number;
  taille: number;
  ecritures: Ecriture[];
}

const TON_ACTION: Record<string, TonPastille> = {
  CONNEXION: 'succes',
  DECONNEXION: 'neutre',
  CONNEXION_REFUSEE: 'refus',
  CREATION: 'interface',
  MODIFICATION: 'attente',
  VALIDATION: 'succes',
  ANNULATION: 'refus',
  SUPPRESSION_LOGIQUE: 'refus',
};

export function JournalAudit() {
  const [page, setPage] = useState(1);
  const [entite, setEntite] = useState('');

  const { donnees, chargement, erreur } = useRessource<Page>(
    `/journal-audit?page=${page}&taille=50${entite ? `&entite=${entite}` : ''}`,
  );
  const entites = useRessource<string[]>('/journal-audit/entites');

  const ecritures = donnees?.ecritures ?? [];
  const total = donnees?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / (donnees?.taille ?? 50)));

  const colonnes: Colonne<Ecriture>[] = [
    {
      cle: 'horodatage',
      entete: 'Horodatage',
      num: true,
      rendu: (e) => formaterDateHeure(e.horodatage),
    },
    {
      cle: 'action',
      entete: 'Action',
      rendu: (e) => <Pastille ton={TON_ACTION[e.action] ?? 'neutre'}>{e.action}</Pastille>,
    },
    { cle: 'entite', entete: 'Entité', rendu: (e) => e.entite },
    { cle: 'entiteId', entete: 'Réf.', num: true, rendu: (e) => e.entiteId ?? '—' },
    {
      cle: 'utilisateur',
      entete: 'Auteur',
      rendu: (e) => (e.utilisateur ? e.utilisateur.login : '—'),
    },
    { cle: 'ip', entete: 'Adresse IP', num: true, rendu: (e) => e.adresseIp ?? '—' },
    { cle: 'commentaire', entete: 'Commentaire', rendu: (e) => e.commentaire ?? '—' },
  ];

  return (
    <>
      <p className="entete__fil">Conformité</p>
      <div className="entete">
        <h1>Journal d’audit</h1>
        <span className="entete__ref">{total} écritures</span>
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(erreur)}
        </BandeauAlerte>
      )}

      <Bloc titre="Écritures" meta={chargement ? 'chargement…' : `page ${page} sur ${pages}`}>
        <BarreFiltres>
          <FiltreSegments
            libelle="Entité"
            valeur={entite}
            onChange={(e) => {
              setEntite(e);
              setPage(1);
            }}
            options={[['', 'Toutes'], ...(entites.donnees ?? []).map((e) => [e, e] as const)]}
          />
        </BarreFiltres>

        <Tableau colonnes={colonnes} lignes={ecritures} cle={(e) => e.id} />
        {donnees && (
          <Pagination
            page={donnees.page}
            taille={donnees.taille}
            total={donnees.total}
            onPage={setPage}
          />
        )}
      </Bloc>
    </>
  );
}
